
import React, {FC, MouseEvent, useState, useRef, useEffect, ChangeEvent, MutableRefObject}  from 'react';
import {useParams} from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { Controller, FieldArray, FieldArrayMethodProps, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { StringIfPlural, useTranslation  } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import { MdOutlineAdd } from 'react-icons/md';


import entityService, { useBasicFilterEntity } from 'features/services/Entity';
import { currentBasicTextFilterPropsAtom, currentFormNameAtom, isSearchBoxShowAtom, isSaveLoadingAtom } from 'library/store';
import useClassTemplateService, { useBasicFilterClassTemplate } from './services/ClassTemplate';
import { useRecoilState } from 'recoil';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { IEntity, IResult } from 'library/interface';
import Button from '@mui/material/Button';
import { Checkbox, FormControlLabel, IconButton, InputAdornment, MenuItem, Tabs, Tab, Typography, Chip, FormHelperText, Link } from '@mui/material';
import NumberFormat from 'react-number-format';

import { IClassTemplate, defaultClassTemplate } from './models/ClassTemplate';


import TextFieldRight from 'components/ui/TextFieldRight';
import { FormDialog } from 'components/ui/FormDialog';
import { BasicTextFilterForm } from 'components/ui/BasicTextFilterForm';
import useEnumerationService, { useBasicFilterEnumeration, useEnumerationItemCreateUpdate } from 'features/configuration/services/Enumeration';


import IEnumeration, { IEnumerationItem, Enum_STUDY_LEVEL, Enum_STUDY_LANGUAGE, Enum_STUDY_SECTION, Enum_STUDY_CYCLE, Enum_TIME_SLOT, Enum_STUDY_TRACK } from 'features/configuration/models/Enumeration';
import {ITarification} from 'features/configuration/models/Tarification';


import { IExtensionType  } from 'features/configuration/models/ExtensionType';
import useExtensionTypeService, { useBasicFilterExtensionType } from 'features/configuration/services/ExtensionType';

import { justifyCenter, typographyGroupBoxStyling } from 'themes/commonStyles';

import EntityExpression from 'components/ui/EntityExpression';
import { isFalsy } from 'utility-types';
import { DialogEnumerationItemForm } from 'features/configuration/DialogEnumerationItemForm';

export const ClassTemplateForm: FC<IClassTemplate> = (props: IClassTemplate = defaultClassTemplate) => {

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();  
  const { enqueueSnackbar } = useSnackbar();

  const {id} = useParams();
  
  const [_id, _setId] = useState<number>( Number( id || 0 ) );

  const {retrieveEntity, retrieveData, openEntityActionDrawer, 
      checkEntityExpressionSyntax, checkEntitySaveAuthorization } = entityService();

  const {getEnumerationItemsByEnumerationCodes } = useEnumerationService();
  
  const { createClassTemplate, updateClassTemplate } = useClassTemplateService();

  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);

  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  const basicFilterClassTemplate = useBasicFilterClassTemplate( 
    (event: React.MouseEvent<unknown>, row: IClassTemplate) => {
        setIsSearchBoxShow(false);
        _setId(row.id);
      }
  );

  const {canAddEnumerationItem, canUpdateEnumerationItem, displayEnumerationItemDialog,
    enumerationItem, hideEnumerationItemDialog, saveEnumerationItem,
    onAddItemClick, onUpdateItemClick } = useEnumerationItemCreateUpdate();

  
  const [openEntityFilter, setOpenEntityFilter] = useState(false);
  const basicFilterEntity = useBasicFilterEntity( 
      (event: React.MouseEvent<unknown>, row: IEntity) => {
          const {name, description} = row;

          // setValue('entityName', name || '');
          // setValue('entityDescription', description || '');
                           
          setOpenEntityFilter(false);
      }
  );

  const [openEnumerationFilter, setOpenEnumerationFilter] = useState(false);
  const basicFilterEnumeration = useBasicFilterEnumeration( 
      (event: React.MouseEvent<unknown>, row: IEnumeration) => {
          const {id, name, description} = row;

          // setValue('enumerationId', id);
          // setValue('enumerationName', name);
                           
          setOpenEnumerationFilter(false);
      }
  );
  

  
  const methods = useForm<IClassTemplate>({defaultValues:defaultClassTemplate});
  const { register, setValue ,getValues, watch, reset ,handleSubmit ,control , formState: { errors } } = methods;

  const watchStudyCycle = watch('studyCycle');
  const watchStudyLanguage = watch('studyLanguage');
  const watchStudySection = watch('studySection');
  const watchStudyLevel = watch('studyLevel');
  const watchStudyTrack = watch('studyTrack');
  const watchPreviousStudyTrack = watch('previousStudyTrack');
   // let { fields, append: appendCoverages, update: updateCoverage ,remove: removeCoverage,  } = useFieldArray({//<O, TName>({ //<O,`billingDetails.${number}.billingDetailTasks`>({
  //   name: `productCoverages`,
  //   control,            
  // });
  

  const queryClient = useQueryClient();
  const {isLoading, isError, isSuccess ,error,mutate } = useMutation<IResult<IClassTemplate>,Error,IClassTemplate>(
      _id>0?updateClassTemplate:createClassTemplate, {   
        onSuccess: (data: IResult<IClassTemplate>) => {
          enqueueSnackbar( t('_Operation_done'), { variant: 'success',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 }); 
                   
          setIsSaveLoading(false);
          _setId(data.data.id);
          //setCurrentEntityIdForAction(data.data.id);
          setCurrentFormNameAtom(`${t('Class template')} - # ${data.data.id} # - `);
          queryClient.invalidateQueries(['ClassTemplate',data.data.id]);
        },
        onError: (err: Error) => {          
          enqueueSnackbar( error?.message, { variant: 'error',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 });
          setIsSaveLoading(false);
        }
      });

    const {data: _data, refetch} = useQuery<IClassTemplate>(['ClassTemplate', _id], () => retrieveEntity('ClassTemplate',_id), 
      {refetchOnWindowFocus: false ,enabled: false } );

      const {data: enumItems, refetch: refetchEnumerationItems} = useQuery<IEnumerationItem[]>(
        ['EnumerationItems', 'ClassTemplate'], () => 
          getEnumerationItemsByEnumerationCodes( [Enum_STUDY_LEVEL, Enum_STUDY_LANGUAGE, 
              Enum_STUDY_SECTION, Enum_STUDY_CYCLE, Enum_TIME_SLOT, Enum_STUDY_TRACK ] ));

        const refEnumItems = useRef<IEnumerationItem[]>();    
        useEffect( () => {   
            refEnumItems.current = enumItems;
          
        }, [enumItems]);

      useEffect( () => {        
        setCurrentFormNameAtom(t('Class template'));
        setCurrentBasicTextFilterProps(basicFilterClassTemplate);
      }, []);    
    
      /********** This use effect call retreive data wich will call refetch and _data will be updated. 
        and the new useEffect will take place ********************/
        useEffect( () => {
            // setCurrentFormName(t('Billing'));        
            
            if(_id > 0)
              retrieveData('ClassTemplate',_id, refetch);  
          }, [_id] );
    
    
        useEffect( () => {
            
        if(_data && _data.id > 0) {
            setCurrentFormNameAtom(`${t('Class template')} - # ${_data.id} # - `);

            reset(_data);
        }
        }, [_data]);
    
      const newData = async (event: MouseEvent<HTMLButtonElement>) => {    
        _setId(0);      
        reset(defaultClassTemplate);    
      }
      
      const saveData = async (event: MouseEvent<HTMLButtonElement>) => {  
        if(!checkEntitySaveAuthorization('ClassTemplate', _id)) {
          setIsSaveLoading(false);
             return;
        }
        

          const data = getValues(); 
          if(data.name.trim() === '' || data.description.trim() === '') {
              enqueueSnackbar( t('Reference is not specified'), { variant: 'warning',
                    anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
                    setIsSaveLoading(false);
                    return;
            }
      
          mutate(data);
      }
    
      const actionData = async (event: MouseEvent<HTMLButtonElement>) => {
        openEntityActionDrawer('ClassTemplate', _id);
      }
      
    const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {          
    //    queryClient.invalidateQueries(['RequestType',currentEntityIdForAction]);        
    //    await retreiveData(currentEntityNameForAction,currentEntityIdForAction, refetch);        
    //    reset(_data);        
    }

  return (
    <FormProvider {...methods} >
            <Box sx={{ mx: 0.1 }}>
                <Grid container rowSpacing={0.5} columnSpacing={0.1}>
                    <Grid item xs={12} md={6} lg={6} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >                        
                        <Stack flexDirection='column'  >
                            <Box sx={{ mt: 1, width: '100%' }} >
                                <Button id='btnNew' onClick={newData} sx={ {display:'none'}}  />                                  
                                <Button id='btnSave' onClick={saveData} sx={ {display:'none'}}  />
                                <Button id='btnAction' onClick={actionData} sx={ {display:'none'}}  />                                                              
                                <Button id='btnAfterAction' onClick={afterAction} sx={ {display:'none'}}  />
                                {/* <TextField sx={{width:'calc(15% - 8px)'}} id="id" label={t('Id')} {...register('id')} inputProps={ {readOnly: true}} />  */}
                                <TextField sx={{width:'calc(50% - 8px)'}} id="name" label={t('Name')} {...register('name')} inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } } />                                
                                <Controller name='level' control={control} 
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(15% - 8px)'}} id="level"
                                        label={t('Order')} inputProps={ {readOnly: false}}  >
                                        {Array.from({ length: 17 }, (_, i) => i + 1).map( 
                                          (x,idx) => <MenuItem key={x} value={x}>{x}</MenuItem> )
                                        } 
                                      </TextField>
                                    )}
                                />
                                <FormControlLabel sx={{width:'calc(35% - 8px)'}}
                                      label={`${t('Is final class ?')}`}
                                      control={
                                      <Controller
                                          name='isFinalClass'
                                          control={control}
                                          render={({field: {value, onChange,...props} }) => <Checkbox {...props} checked={value} onChange={onChange} />}                        
                                  />} />
                            </Box>   
                            <Box sx={{ mt: 1, width: '100%' }} >
                              
                              <TextField sx={{width:'calc(65% - 8px)'}} id="description" label={t('Description')} {...register('description')} inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } } />                                
                              <Controller name='timeSlotCategory' control={control} 
                                    
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(35% - 8px)'}} id="timeSlotCategory"
                                        label={t('Time slot')} inputProps={ {readOnly: false}} 
                                         >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_TIME_SLOT ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                />
                            </Box>                                                   
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <Controller name='studySection' control={control} 
                                  render={ ({field: {onChange, value}}) => (
                                    <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="studySection"
                                      label={t('Section')} inputProps={ {readOnly: false}} 
                                      helperText={ 
                                        <FormHelperText>                                    
                                          { canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_STUDY_SECTION, 
                                                                t('Study section'), t('Study section'))}
                                            sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                            {t('Add')}
                                          </Link> }
                                          { (canUpdateEnumerationItem && !isFalsy(watchStudySection) ) && <Link href="#" onClick={(event) => {
                                                  onUpdateItemClick( Enum_STUDY_SECTION, t('Study section'), t('Study section'),
                                                        refEnumItems.current?.find(x => x.enumerationCode === Enum_STUDY_SECTION && x.code === watchStudySection)
                                                      ); }}
                                            sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                            {t('Update')}
                                          </Link> }
                                        </FormHelperText>
                                      } >
                                      {enumItems && enumItems.filter( e => 
                                            e.enumerationCode === Enum_STUDY_SECTION ).map( 
                                        (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                      }
                                    </TextField>
                                  )}
                                />                                
                              <Controller name='studyLanguage' control={control} 
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="studyLanguage"
                                    label={t('Study language')} inputProps={ {readOnly: false}} 
                                    helperText={ 
                                      <FormHelperText>                                    
                                        { canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_STUDY_LANGUAGE, 
                                                              t('Study language'), t('Study language'))}
                                          sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                          {t('Add')}
                                        </Link> }
                                        { (canUpdateEnumerationItem && !isFalsy(watchStudyLanguage) ) && <Link href="#" onClick={(event) => {
                                                onUpdateItemClick( Enum_STUDY_LANGUAGE, t('Study language'), t('Study language'),
                                                      refEnumItems.current?.find(x => x.enumerationCode === Enum_STUDY_LANGUAGE && x.code === watchStudyLanguage)
                                                    ); }}
                                          sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                          {t('Update')}
                                        </Link> }
                                      </FormHelperText>
                                    } >
                                    {enumItems && enumItems.filter( e => 
                                          e.enumerationCode === Enum_STUDY_LANGUAGE ).map( 
                                      (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                    }
                                  </TextField>
                                )}
                                />
                            </Box>
                            <Box sx={{ mt: 1, width: '100%' }} >
                                <Controller name='studyCycle' control={control} 
                                  render={ ({field: {onChange, value}}) => (
                                    <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="studyCycle"
                                      label={t('Cycle')} inputProps={ {readOnly: false}} 
                                      helperText={ 
                                        <FormHelperText>                                    
                                          { canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_STUDY_CYCLE, 
                                                                t('Study cycle'), t('Study cycle'))}
                                            sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                            {t('Add')}
                                          </Link> }
                                          { (canUpdateEnumerationItem && !isFalsy(watchStudyCycle) ) && <Link href="#" onClick={(event) => {
                                                  onUpdateItemClick( Enum_STUDY_CYCLE, t('Cycle'), t('Cycle'),
                                                        refEnumItems.current?.find(x => x.enumerationCode === Enum_STUDY_CYCLE && x.code === watchStudyCycle)
                                                      ); }}
                                            sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                            {t('Update')}
                                          </Link> }
                                        </FormHelperText>
                                      } >
                                      {enumItems && enumItems.filter( e => 
                                            e.enumerationCode === Enum_STUDY_CYCLE ).map( 
                                        (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                      }
                                    </TextField>
                                  )}
                              />
                              <Controller name='studyLevel' control={control} 
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="studyLevel"
                                        label={t('Level')} inputProps={ {readOnly: false}} 
                                        helperText={ 
                                          <FormHelperText>                                    
                                            { canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_STUDY_LEVEL, 
                                                                  t('Study level'), t('Study level'))}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                              {t('Add')}
                                            </Link> }
                                            { (canUpdateEnumerationItem && !isFalsy(watchStudyLevel) ) && <Link href="#" onClick={(event) => {
                                                   onUpdateItemClick( Enum_STUDY_LEVEL, t('Study level'), t('Study level'),
                                                          refEnumItems.current?.find(x => x.enumerationCode === Enum_STUDY_LEVEL && x.code === watchStudyLevel)
                                                        ); }}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                              {t('Update')}
                                            </Link> }
                                          </FormHelperText>
                                        } >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_STUDY_LEVEL ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                />
                            </Box>    
                            <Box sx={{ mt: 1, width: '100%' }} >
                                <Controller name='studyTrack' control={control} 
                                  render={ ({field: {onChange, value}}) => (
                                    <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="studyCycle"
                                      label={t('Study track')} inputProps={ {readOnly: false}} 
                                      helperText={ 
                                        <FormHelperText>                                    
                                          { canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_STUDY_TRACK, 
                                                                t('Study track'), t('Study track'))}
                                            sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                            {t('Add')}
                                          </Link> }
                                          { (canUpdateEnumerationItem && !isFalsy(watchStudyTrack) ) && <Link href="#" onClick={(event) => {
                                                  onUpdateItemClick( Enum_STUDY_TRACK, t('Study track'), t('Study track'),
                                                        refEnumItems.current?.find(x => x.enumerationCode === Enum_STUDY_TRACK && x.code === watchStudyTrack)
                                                      ); }}
                                            sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                            {t('Update')}
                                          </Link> }
                                        </FormHelperText>
                                      } >
                                      {enumItems && enumItems.filter( e => 
                                            e.enumerationCode === Enum_STUDY_TRACK ).map( 
                                        (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                      }
                                    </TextField>
                                  )}
                              />
                              <Controller name='previousStudyTrack' control={control} 
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="studyLevel"
                                        label={t('Level')} inputProps={ {readOnly: false}} 
                                        helperText={ 
                                          <FormHelperText>                                    
                                            { canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_STUDY_TRACK, 
                                                                  t('Study level'), t('Study level'))}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                              {t('Add')}
                                            </Link> }
                                            { (canUpdateEnumerationItem && !isFalsy(watchPreviousStudyTrack) ) && <Link href="#" onClick={(event) => {
                                                   onUpdateItemClick( Enum_STUDY_TRACK, t('Study level'), t('Study level'),
                                                          refEnumItems.current?.find(x => x.enumerationCode === Enum_STUDY_TRACK && x.code === watchPreviousStudyTrack)
                                                        ); }}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                              {t('Update')}
                                            </Link> }
                                          </FormHelperText>
                                        } >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_STUDY_TRACK ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                />
                            </Box>                         
                            { displayEnumerationItemDialog && <DialogEnumerationItemForm 
                              {...{open: displayEnumerationItemDialog, 
                                    title: '', 
                                    enumerationItem, hideEnumerationItemDialog, saveEnumerationItem,
                                    afterSave: async () => {await refetchEnumerationItems()}}} 
                              />
                          }
                        </Stack>                        
                      </Grid>
                    <Grid item xs={12} md={6} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
                      
                    </Grid>     
                                                  
                </Grid>
            </Box>
        </FormProvider> 
  )
}

